import React, { useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { ErrorType } from "@/types";
import { SaleTaxTable } from "@/types/system-setting/sale-tax";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

import {
  useGetSalesTaxQuery,
  useUpdateSalesTaxMutation,
} from "@/services/salesTaxApi";

import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { displayValue } from "@/lib/helper";
import { SaleTax } from "@/types/system-setting/sale-tax";
import { Status } from "@/types/enums";
import SaleTaxForm from "../modal/form";
import TableSearchFilter from "@/components/ui/table/data-table-search";

export default function SaleTaxesTableView() {
  const [sale_tax, setSaleTax] = useState<"read" | "write" | "no_access" | "full_access">("no_access");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<SaleTax | undefined>(undefined);
  const [action, setAction] = useState<"add" | "edit">("add");

  useEffect(() => {
    try {
      const stored = localStorage.getItem("accessLevels");
      if (stored) {
        const parsed = JSON.parse(stored);
        setSaleTax(parsed?.sale_tax ?? "no_access");
      }
    } catch {
      setSaleTax("no_access");
    }
  }, []);

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData({
      limit: 10,
      offset: 0,
      sort_order: "desc",
      sort_key: "id",
    });

  const {
    data: saleTaxesData,
    isLoading,
    refetch,
    error,
  } = useGetSalesTaxQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );
  const [updateSaleTax, { isLoading: saleTaxLoading }] =
    useUpdateSalesTaxMutation();

  const handleCloseDailog = React.useCallback(() => setIsDialogOpen(false), []);

  const saletaxestableData = React.useMemo(() => {
    return Array.isArray(saleTaxesData?.data) ? saleTaxesData?.data : [];
  }, [saleTaxesData]);

  const { toast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});


  const handleStatusChange = React.useCallback(async (payload: SaleTax) => {
    try {
      const resp = await updateSaleTax(payload).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Tax/VAT Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  }, [updateSaleTax, refetch, toast]);

  const handleEditSaleTax = React.useCallback((data: SaleTax) => {
    // Convert SaleTax to SaleTax
    const saleTaxData: SaleTax = {
      id: data.id,
      name: data.name,
      percentage: data.percentage,
      status: data.status as Status,
      org_id: data.org_id,
      is_srb: data.is_srb,
    };
    setFormData(saleTaxData);
    setAction("edit");
    setIsDialogOpen(true);
  }, [setFormData, setAction, setIsDialogOpen]);

  const actionsColumn: ColumnDef<SaleTax> = React.useMemo(() => ({
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={sale_tax}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEditSaleTax}
      />
    ),
  }), [sale_tax, refetch, handleEditSaleTax]);

  const columns: ColumnDef<SaleTax>[] = React.useMemo(() => [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tax/VAT Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="w-fit">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.name}`.length > 20
                          ? `${row.original.name}`.substring(0, 20) + "..."
                          : `${row.original.name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent className="mr-[16.5rem]">
                  <p className="captilaize text-sm">
                    {displayValue(`${row?.original?.name}`)}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "percentage",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Percentage"
          sortKey="percentage"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.percentage + "%"}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <Select
          value={row.original?.status}
          onValueChange={(e) => {
            handleStatusChange({ ...row.original, status: e as Status });
          }}
          disabled={sale_tax == "read"}
        >
          <SelectTrigger
            className="h-8 max-w-36"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              console.log("SelectTrigger mouseDown");
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              console.log("SelectTrigger pointerDown");
            }}
            onClick={(e) => {
              console.log("SelectTrigger clicked");
            }}
          >
            <SelectValue placeholder="Status" className="text-gray-400">
              <span className="flex gap-2 items-center">
                <span
                  className={`${row.original?.status == Status.Active ? "bg-green-500" : "bg-blue-500"} rounded-[50%] w-4 h-4`}
                ></span>
                <span>{row.original?.status == Status.Active ? "Active" : "Inactive"}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value={Status.Active}
            >
              Active
            </SelectItem>
            <SelectItem
              value={Status.Inactive}
            >
              Inactive
            </SelectItem>
          </SelectContent>
        </Select>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    ...(sale_tax !== "read" ? [actionsColumn] : []),
  ], [sale_tax, refetch, handleEditSaleTax]);

  const table = useReactTable({
    data: saletaxestableData as SaleTax[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });


  const handleAddSaleTax = React.useCallback(() => {
    setFormData(undefined);
    setAction("add");
    setIsDialogOpen(true);
  }, [setFormData, setAction, setIsDialogOpen]);

  const totalRecords = saleTaxesData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        {/* Table Filter component */}
        <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />

        <div>
          {sale_tax !== "read" && (
            <Button
              className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
              onClick={handleAddSaleTax}
            >
              <PlusIcon className="h-4 w-4" />
              Create New
            </Button>
          )}
        </div>
      </div>
      <div className="rounded-none border border-border z-0">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (sale_tax !== "read" && e.target instanceof Element) {
                        const target = e.target as Element;
                        if (!target.closest('[role="checkbox"]') && (e.target === e.currentTarget || target.closest('td'))) {
                          handleEditSaleTax(row.original);
                        }
                      }
                    }}
                    className={`${sale_tax !== "read" ? "cursor-pointer hover:bg-secondary" : ""}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {saletaxestableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <SaleTaxForm
        data={formData}
        setData={setFormData}
        action={action}
        setAction={setAction}
        refetch={refetch}
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
      />
    </div>
  );
}

